import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Header } from '@/components/common/Header/Header';
import { Footer } from '@/components/common/Footer/Footer';
import { User, MapPin, ShoppingBag, Heart, LogOut } from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions } from '@/store/slices/authSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { authService } from '@/services/api/auth.service';
import { generateId } from '@/utils/helpers';

const PROFILE_NAV = [
  { label: 'DASHBOARD', href: ROUTES.PROFILE, icon: User, end: true },
  { label: 'ADDRESSES', href: ROUTES.PROFILE_ADDRESSES, icon: MapPin, end: false },
  { label: 'ORDERS', href: ROUTES.PROFILE_ORDERS, icon: ShoppingBag, end: false },
  { label: 'WISHLIST', href: ROUTES.PROFILE_WISHLIST, icon: Heart, end: false },
];

export function ProfileLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const handleLogout = async () => {
    await authService.logout();
    dispatch(authActions.logout());
    dispatch(uiActions.addToast({ id: generateId(), message: 'Logged out successfully.', type: 'success' }));
    navigate(ROUTES.HOME);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row">
            <aside className="w-full shrink-0 lg:w-64">
              <h1 className="mb-6 text-2xl font-bold text-text-primary">MY ACCOUNT</h1>
              {/* Profile Summary Card */}
              <div className="mb-4 flex flex-col items-center rounded-[var(--radius-md)] bg-white p-5 border border-border-light text-center shadow-xs">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-xl font-bold uppercase text-white shadow-sm mb-3">
                  {user?.firstName?.[0] || 'U'}
                </div>
                <h3 className="font-bold text-text-primary text-base leading-tight">
                  {user?.firstName} {user?.lastName}
                </h3>
                <span className="text-xs text-text-secondary mt-1 max-w-[180px] truncate block">
                  {user?.email}
                </span>
              </div>

              <nav className="rounded-[var(--radius-md)] bg-white p-4 shadow-xs border border-border-light">
                <ul className="space-y-1">
                  {PROFILE_NAV.map((item) => (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        end={item.end}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-accent-light text-accent'
                              : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary'
                          }`
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-4 flex w-full items-center gap-3 border-t border-border px-3 pt-4 text-sm font-medium text-text-secondary transition-colors hover:text-error"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </nav>
            </aside>
            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
